import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import { TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';

export default function ProfitCalculator({ totalCost, quotedPrice, onChange }) {
  const [quote, setQuote] = useState(quotedPrice || 0);

  // Sync with external quotedPrice changes
  useEffect(() => {
    if (quotedPrice !== undefined && quotedPrice !== quote) {
      setQuote(quotedPrice);
    }
  }, [quotedPrice]);

  const metrics = useMemo(() => {
    const cost = totalCost || 0;
    const price = quote || 0;
    const profit = price - cost;
    const margin = price > 0 ? ((profit / price) * 100) : 0;
    const markup = cost > 0 ? ((profit / cost) * 100) : 0;

    return {
      cost,
      price,
      profit,
      margin,
      markup,
      isHealthy: margin >= 30,
      isWarning: margin < 30 && margin >= 20,
      isLow: margin < 20
    };
  }, [totalCost, quote]);

  const handleQuoteChange = (value) => {
    const newQuote = parseFloat(value) || 0;
    setQuote(newQuote);
    if (onChange) {
      onChange(newQuote);
    }
  };

  const applyMargin = (targetMargin) => {
    if (!totalCost) return;
    // Formula: Price = Cost / (1 - Margin/100)
    const newPrice = totalCost / (1 - targetMargin / 100);
    handleQuoteChange(newPrice.toFixed(2));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          Profit Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Quote Input */}
        <div>
          <Label>Customer Quote Amount</Label>
          <Input
            type="number"
            step="0.01"
            value={quote}
            onChange={(e) => handleQuoteChange(e.target.value)}
            placeholder="Enter quoted price"
            className="text-lg font-semibold mt-1"
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => applyMargin(25)}
              className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded"
            >
              25% Margin
            </button>
            <button
              onClick={() => applyMargin(35)}
              className="text-xs px-2 py-1 bg-green-100 hover:bg-green-200 rounded"
            >
              35% Margin
            </button>
            <button
              onClick={() => applyMargin(50)}
              className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded"
            >
              50% Margin
            </button>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="space-y-3">
          <div className="flex justify-between p-3 bg-red-50 rounded-lg">
            <span className="text-slate-700">Total Cost</span>
            <span className="font-semibold text-red-700">
              -${metrics.cost.toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
            <span className="text-slate-700">Quote Amount</span>
            <span className="font-semibold text-blue-700">
              ${metrics.price.toFixed(2)}
            </span>
          </div>

          <div className={`flex justify-between p-4 rounded-lg border-2 ${
            metrics.isHealthy ? 'bg-green-50 border-green-300' :
            metrics.isWarning ? 'bg-yellow-50 border-yellow-300' :
            'bg-red-50 border-red-300'
          }`}>
            <span className="font-semibold">Net Profit</span>
            <span className={`text-xl font-bold ${
              metrics.isHealthy ? 'text-green-700' :
              metrics.isWarning ? 'text-yellow-700' :
              'text-red-700'
            }`}>
              ${metrics.profit.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg text-center">
            <div className="text-sm text-slate-600 mb-1">Profit Margin</div>
            <div className={`text-2xl font-bold ${
              metrics.isHealthy ? 'text-green-600' :
              metrics.isWarning ? 'text-yellow-600' :
              'text-red-600'
            }`}>
              {metrics.margin.toFixed(1)}%
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg text-center">
            <div className="text-sm text-slate-600 mb-1">Markup</div>
            <div className="text-2xl font-bold text-slate-700">
              {metrics.markup.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Health Indicator */}
        <div className={`flex items-start gap-3 p-4 rounded-lg ${
          metrics.isHealthy ? 'bg-green-50' :
          metrics.isWarning ? 'bg-yellow-50' :
          'bg-red-50'
        }`}>
          {metrics.isHealthy ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              metrics.isWarning ? 'text-yellow-600' : 'text-red-600'
            }`} />
          )}
          <div className="text-sm">
            {metrics.isHealthy && (
              <>
                <div className="font-semibold text-green-900 mb-1">Healthy Margin</div>
                <div className="text-green-700">
                  Your profit margin is above 30%, which is excellent for this type of work.
                </div>
              </>
            )}
            {metrics.isWarning && (
              <>
                <div className="font-semibold text-yellow-900 mb-1">Low Margin Warning</div>
                <div className="text-yellow-700">
                  Your margin is between 20-30%. Consider increasing the quote to improve profitability.
                </div>
              </>
            )}
            {metrics.isLow && (
              <>
                <div className="font-semibold text-red-900 mb-1">Margin Too Low</div>
                <div className="text-red-700">
                  Your margin is below 20%. This quote may not be profitable after overhead and unexpected costs.
                </div>
              </>
            )}
          </div>
        </div>

        {/* Quick Reference */}
        <div className="border-t pt-4 space-y-2 text-sm text-slate-600">
          <div className="font-semibold text-slate-900 mb-2">Industry Benchmarks:</div>
          <div>• Minimum profitable margin: 20-25%</div>
          <div>• Target margin: 35-40%</div>
          <div>• Premium service margin: 45-55%</div>
        </div>
      </CardContent>
    </Card>
  );
}
